(ns app.agenda.post-settings
  "The settings that the user chose when scheduling a post, as
   `[label value]` rows for the agenda's preview modal: a switch has a
   boolean value, everything else a string. The labels are the ones of
   the scheduling forms (`app.social-scheduling.page` and the post type
   options of `app.bundle-social.platforms`)."
  (:require [app.agenda.home :as home]
            [app.bundle-social.platforms :as social-platforms]
            [clojure.string :as str]))

(defn choice-label
  "The label of the `value` of a choice option, or the value itself."
  [option value]
  (or (some #(when (= value (:choice/value %)) (:choice/label %))
            (:option/choices option))
      value))

(defn option-value
  "The display value of the bundle.social post type `option` for the
   chosen `options`, nil for an empty text."
  [option options]
  (let [value (get options (:option/key option))]
    (case (:option/type option)
      :checkbox (boolean value)
      ;; TikTok's cover frame is the only number option:
      :number (when value
                (if (= :cover-ms (:option/key option))
                  (str (/ value 1000) " s")
                  (str value)))
      (:text :url) (not-empty value)
      (some->> value (choice-label option)))))

(defn account-key
  "Instagram Stories carry no `:schedule/account-key`, only the user id."
  [post]
  (or (:schedule/account-key post)
      (some->> (:instagram.story.post/user-id post)
               (str "instagram:"))))

(defn account
  "The post's account from the agenda's `:schedule/accounts`."
  [home-result post]
  (let [k (account-key post)]
    (some #(when (= k (:account/key %)) %)
          (:schedule/accounts home-result))))

(defn highlight-title
  "The title of the story's highlight from the
   `:query/social-scheduling-highlights` result. Without highlights (still
   loading, or the account's data is not synced) it only says that one
   is selected."
  [highlights highlight-id]
  (if (seq highlights)
    (or (some #(when (= highlight-id (:highlight/id %)) (:highlight/title %))
              highlights)
        "Deleted highlight")
    "Selected"))

(defn story-rows
  [post highlights]
  (let [highlight-id (not-empty (:instagram.story.post/highlight-id post))]
    [(when highlight-id
       ["Highlight" (highlight-title highlights highlight-id)])
     ;; The page's name when it is known:
     ["Share to Facebook"
      (if (:instagram.story.post.job/share-to-fb? post)
        (or (:schedule/share-to-fb-name post) true)
        false)]
     ["First comment" (not-empty (:instagram.story.post/first-comment post))]
     ["AI-generated content" (boolean (:instagram.story.post/ai-generated? post))]]))

(defn reel-rows
  [post]
  [["Caption" (not-empty (:reel/caption post))]
   ["Show Reel in Instagram posts" (true? (:reel/recommend-feed post))]
   ["AI-generated content" (boolean (:reel/ai-generated? post))]])

(defn tiktok-photo?
  [post]
  (= :type.instagram.story/image
     (get-in post [:instagram.story.post/media :type])))

(defn catalog-option
  "The option `k` of a post type in the `app.bundle-social.platforms`
   catalog, for its choice labels."
  [id type-id k]
  (some #(when (= k (:option/key %)) %)
        (social-platforms/options id type-id)))

(def tiktok-privacy-option
  (catalog-option "TIKTOK" "POST" :privacy))

(defn tiktok-rows
  [post]
  (let [photo? (tiktok-photo? post)
        title (not-empty (:tiktok.post/title post))
        description (not-empty (:tiktok.post/description post))]
    (concat
      (if photo?
        [["Photo title" title]
         ["Photo description" description]]
        ;; The scheduling form sends a video's caption as the title
        ;; and the description, the TikTok form as the title only:
        [["Caption" (or title description)]])
      [["Who can view this post"
        (some->> (:tiktok.post/privacy-level post)
                 (choice-label tiktok-privacy-option))]
       ["Disable comments" (boolean (:tiktok.post/disable-comment? post))]]
      (if photo?
        [["Auto Audio" (boolean (:tiktok.post/auto-audio? post))]]
        [["Disable Duet" (boolean (:tiktok.post/disable-duet? post))]
         ["Disable Stitch" (boolean (:tiktok.post/disable-stitch? post))]])
      [["AI Generated" (boolean (:tiktok.post/ai-generated? post))]
       ["Partnership" (boolean (:tiktok.post/branded-content? post))]
       ["Branded Content" (boolean (:tiktok.post/brand-organic? post))]])))

(def youtube-privacy-option
  (catalog-option "YOUTUBE" "SHORT" :privacy))

(defn youtube-rows
  [post]
  [["Title" (not-empty (:youtube.short/title post))]
   ["Description" (not-empty (:youtube.short/description post))]
   ["Visibility" (some->> (:youtube.short/privacy-status post)
                          (choice-label youtube-privacy-option))]
   ["Made for kids" (boolean (:youtube.short/made-for-kids? post))]
   ["Altered or synthetic media" (boolean (:youtube.short/synthetic-media? post))]])

(defn social-post-rows
  [post]
  (let [id (:social-platform/id (home/social-platform post))
        type-id (:schedule/post-type post)
        options (:social.post/options post)]
    ;; The scheduling form stores the shared caption for every
    ;; destination, also for post types that publish none (a Story):
    (cons (when (:post-type/caption? (social-platforms/post-type id type-id))
            ["Caption" (not-empty (:social.post/text post))])
          (map (fn [option]
                 [(:option/label option) (option-value option options)])
               (social-platforms/options id type-id)))))

(def text-labels
  "The rows with a text that the user wrote, which the modal shows as a
   quote whatever its length."
  #{"Caption" "Description" "Photo description" "First comment"})

(defn rows
  "The settings of the `post` as `[label value]` rows, without the empty
   ones. `highlights` is the `:instagram.user/highlights` result of the
   story's account."
  [post highlights]
  (->> (cond
         (home/social-platform post) (social-post-rows post)
         (home/reel-post? post) (reel-rows post)
         (home/tiktok-post? post) (tiktok-rows post)
         (home/youtube-post? post) (youtube-rows post)
         :else (story-rows post highlights))
       (remove nil?)
       (filter (fn [[_ value]]
                 (and (some? value)
                      (not (str/blank? (str value))))))))
